import { localizeCatalogMessage } from './runtimeMessages'
import i18n from '@/i18n'

type TFn = (key: string | string[], options?: any) => string

/**
 * Central Arabic auto-translation for runtime messages.
 * Covers: i18n keys, Laravel validation sentences, yup frontend messages
 * ("<Field> is required"), and generic backend CRUD success/error sentences.
 * Only pattern-maps to Arabic when locale is 'ar'; otherwise returns input.
 */
export function translateApiErrorMessage(rawMessage: string, t: TFn): string {
  if (!rawMessage || typeof rawMessage !== 'string') return rawMessage

  const fixedLanguage = (t as TFn & { lng?: string }).lng
  if (fixedLanguage && fixedLanguage !== i18n.language) t = i18n.t.bind(i18n)
  const trimmed = rawMessage.trim()

  const direct = t(trimmed)
  if (direct && direct !== trimmed) return direct
  const catalogMessage = localizeCatalogMessage(trimmed, t, (value, translate) => i18n.language.split('-')[0] === 'ar' ? translateAttributeName(value, translate) : translate(value))
  if (catalogMessage !== undefined) return catalogMessage

  // Pattern-based Arabic mappings below only apply when Arabic is active.
  if (i18n.language.split('-')[0] !== 'ar') {
    return trimmed
  }

  // 2. Common Laravel validation sentences
  const reqMatch = trimmed.match(/^The (.+) field is required\.?$/i)
  if (reqMatch) {
    const field = translateAttributeName(reqMatch[1], t)
    return `حقل ${field} مطلوب`
  }

  const minMatch = trimmed.match(/^The (.+) must be at least (.+)\.?$/i)
  if (minMatch) {
    const field = translateAttributeName(minMatch[1], t)
    return `يجب ألا يقل ${field} عن ${minMatch[2]}`
  }

  const maxMatch = trimmed.match(/^The (.+) must not be greater than (.+)\.?$/i)
  if (maxMatch) {
    const field = translateAttributeName(maxMatch[1], t)
    return `يجب ألا يزيد ${field} عن ${maxMatch[2]}`
  }

  const takenMatch = trimmed.match(/^The (.+) has already been taken\.?$/i)
  if (takenMatch) {
    const field = translateAttributeName(takenMatch[1], t)
    return `قيمة ${field} مستخدمة بالفعل`
  }

  const invalidMatch = trimmed.match(/^The selected (.+) is invalid\.?$/i)
  if (invalidMatch) {
    const field = translateAttributeName(invalidMatch[1], t)
    return `قيمة ${field} المحددة غير صالحة`
  }

  const emailMatch = trimmed.match(/^The (.+) must be a valid email address\.?$/i)
  if (emailMatch) {
    const field = translateAttributeName(emailMatch[1], t)
    return `يجب أن يكون ${field} بريداً إلكترونياً صالحاً`
  }

  const numMatch = trimmed.match(/^The (.+) must be a number\.?$/i)
  if (numMatch) {
    const field = translateAttributeName(numMatch[1], t)
    return `يجب أن يكون ${field} رقماً صالحاً`
  }

  const dateMatch = trimmed.match(/^The (.+) must be a date\.?$/i)
  if (dateMatch) {
    const field = translateAttributeName(dateMatch[1], t)
    return `يجب أن يكون ${field} تاريخاً صالحاً`
  }

  // 2b. Yup frontend validation sentences
  const yupRequired = trimmed.match(/^(.+?) is required\.?$/i)
  if (yupRequired && !/^the /i.test(trimmed)) {
    const field = translateAttributeName(yupRequired[1], t)
    return `حقل ${field} مطلوب`
  }

  const yupRequiredWhen = trimmed.match(/^(.+?) is required when (.+)$/i)
  if (yupRequiredWhen) {
    const field = translateAttributeName(yupRequiredWhen[1], t)
    return `حقل ${field} مطلوب`
  }

  const yupMinChars = trimmed.match(/^(.+?) must be at least (\d+).*$/i)
  if (yupMinChars) {
    const field = translateAttributeName(yupMinChars[1], t)
    return `يجب ألا يقل ${field} عن ${yupMinChars[2]} أحرف`
  }

  const yupMaxChars = trimmed.match(/^(.+?) must not exceed (\d+).*$/i)
  if (yupMaxChars) {
    const field = translateAttributeName(yupMaxChars[1], t)
    return `يجب ألا يزيد ${field} عن ${yupMaxChars[2]} حرف`
  }

  const yupMaxCharsAlt = trimmed.match(/^(.+?) must be at most (\d+).*$/i)
  if (yupMaxCharsAlt) {
    const field = translateAttributeName(yupMaxCharsAlt[1], t)
    return `يجب ألا يزيد ${field} عن ${yupMaxCharsAlt[2]}`
  }

  const yupPositive = trimmed.match(/^(.+?) must be positive\.?$/i)
  if (yupPositive) {
    const field = translateAttributeName(yupPositive[1], t)
    return `يجب أن يكون ${field} أكبر من صفر`
  }

  const yupNumber = trimmed.match(/^(.+?) must be a ['"]?number['"]?\.?$/i)
  if (yupNumber) {
    const field = translateAttributeName(yupNumber[1], t)
    return `يجب أن يكون ${field} رقماً صالحاً`
  }

  const yupMinNumber = trimmed.match(/^(.+?) must be greater than or equal to (.+?)\.?$/i)
  if (yupMinNumber) {
    const field = translateAttributeName(yupMinNumber[1], t)
    return `يجب ألا يقل ${field} عن ${yupMinNumber[2]}`
  }

  // 2c. Backend success toasts
  const successCrud = trimmed.match(
    /^(.+?) (created|updated|deleted|retrieved|fetched|saved|cleared|sent|queued|posted|approved|rejected|closed|opened|generated)( successfully)?[.!]*$/i,
  )
  if (successCrud) {
    const subject = translateAttributeName(successCrud[1], t)
    const verb = successCrud[2].toLowerCase()
    const VERB_AR: Record<string, string> = {
      created: 'تم إنشاء',
      updated: 'تم تحديث',
      deleted: 'تم حذف',
      retrieved: 'تم جلب',
      fetched: 'تم جلب',
      saved: 'تم حفظ',
      cleared: 'تم مسح',
      sent: 'تم إرسال',
      queued: 'تمت إضافة',
      posted: 'تم ترحيل',
      approved: 'تم اعتماد',
      rejected: 'تم رفض',
      closed: 'تم إغلاق',
      opened: 'تم فتح',
      generated: 'تم إنشاء',
    }
    return `${VERB_AR[verb] || 'تم'} ${subject} بنجاح`
  }

  const successCrudBang = trimmed.match(/^(.+?) (Created|Updated|Deleted) !!$/)
  if (successCrudBang) {
    const subject = translateAttributeName(successCrudBang[1], t)
    const VERB_AR_BANG: Record<string, string> = {
      Created: 'تم إنشاء',
      Updated: 'تم تحديث',
      Deleted: 'تم حذف',
    }
    return `${VERB_AR_BANG[successCrudBang[2]]} ${subject} بنجاح`
  }

  const errorDoing = trimmed.match(
    /^Error (retrieving|creating|updating|deleting|fetching|clearing|sending|queuing|generating|processing) (.+?)\.?$/i,
  )
  if (errorDoing) {
    const subject = translateAttributeName(errorDoing[2], t)
    return `خطأ أثناء ${subject}. يرجى المحاولة لاحقاً.`
  }

  // 3. Known framework message mappings
  const FRAMEWORK_ERROR_MAP: Record<string, string> = {
    'The given data was invalid.': 'البيانات المدخلة غير صالحة.',
    'Unauthenticated.': 'انتهت الجلسة أو غير مصرح. يرجى تسجيل الدخول مجدداً.',
    Unauthenticated: 'انتهت الجلسة أو غير مصرح. يرجى تسجيل الدخول مجدداً.',
    'This action is unauthorized.': 'هذا الإجراء غير مصرح به للمستخدم الحالي.',
    'User does not have the right permissions.': 'ليس لديك الصلاحيات الكافية للقيام بهذا الإجراء.',
    'Server Error': 'حدث خطأ في الخادم. يرجى المحاولة لاحقاً.',
    'Internal Server Error': 'حدث خطأ داخلي في الخادم.',
    'Network Error': 'تعذر الاتصال بالخادم. يرجى التحقق من اتصال الشبكة.',
    'Request Timeout': 'انتهت مهلة الطلب',
    'The request took too long. Please try again.': 'استغرق الطلب وقتاً طويلاً. يرجى المحاولة مرة أخرى.',
    'Network error': 'تعذر الاتصال بالخادم. يرجى التحقق من اتصال الشبكة.',
    'timeout of 10000ms exceeded': 'انتهت مهلة انتظار استجابة الخادم.',
    'Not Found': 'المورد المطلوب غير موجود.',
    'Record not found': 'السجل المطلوب غير موجود.',
    'Record not found.': 'السجل المطلوب غير موجود.',
    'Concurrency conflict': 'تعارض في التعديل المتزامن. يرجى تحديث الصفحة والمحاولة مرة أخرى.',
    'Insufficient stock': 'كمية المخزون المتاحة غير كافية لإتمام العملية.',
    'Invalid email format': 'صيغة البريد الإلكتروني غير صالحة',
    'Invalid Data': 'بيانات غير صالحة',
    Failed: 'فشل',
    Error: 'خطأ',
    Success: 'تم بنجاح',
    Done: 'تم بنجاح',
    Loading: 'جارٍ المعالجة…',
    'Delete.Title': 'هل أنت متأكد؟',
    'Are you sure?': 'هل أنت متأكد؟',
    'Delete.Text': 'لن تتمكن من استعادة هذا السجل!',
    'You will not be able to recover this!': 'لن تتمكن من استعادة هذا السجل!',
    'Delete.confirmButtonText': 'نعم، احذف!',
    'Yes, delete it!': 'نعم، احذف!',
    'Delete.cancelButtonText': 'لا، إلغاء!',
    'No, cancel!': 'لا، إلغاء!',
    Cancel: 'إلغاء',
    Confirm: 'تأكيد',
    Save: 'حفظ',
    'Data retrieved successfully.': 'تم جلب البيانات بنجاح',
    'Settings Updated !!': 'تم تحديث الإعدادات بنجاح',
    'Pos settings Updated !!': 'تم تحديث إعدادات نقاط البيع بنجاح',
    'Cache cleared successfully.': 'تم مسح ذاكرة التخزين المؤقت بنجاح',
    'Settings retrieved successfully.': 'تم جلب الإعدادات بنجاح',
  }

  if (FRAMEWORK_ERROR_MAP[trimmed]) {
    return FRAMEWORK_ERROR_MAP[trimmed]
  }

  return trimmed
}

export function translateAttributeName(attr: string, t: TFn): string {
  const normalizedKey = attr.trim().replace(/\s+/g, '_').toLowerCase()
  const fromT = t(normalizedKey)
  if (fromT && fromT !== normalizedKey) return fromT

  const ATTR_AR_MAP: Record<string, string> = {
    name: 'الاسم',
    code: 'الرمز',
    date: 'التاريخ',
    warehouse_id: 'المستودع',
    'warehouse id': 'المستودع',
    warehouse: 'المستودع',
    client_id: 'العميل',
    'client id': 'العميل',
    customer_id: 'العميل',
    customer: 'العميل',
    supplier_id: 'المورد',
    'supplier id': 'المورد',
    supplier: 'المورد',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    'password confirmation': 'تأكيد كلمة المرور',
    phone: 'رقم الهاتف',
    amount: 'المبلغ',
    'total amount': 'المبلغ الإجمالي',
    'paid amount': 'المبلغ المدفوع',
    'monthly installment': 'القسط الشهري',
    qty: 'الكمية',
    quantity: 'الكمية',
    price: 'السعر',
    unit_price: 'سعر الوحدة',
    cost: 'التكلفة',
    tax_rate: 'نسبة الضريبة',
    category_id: 'الفئة',
    category: 'الفئة',
    'deposit category': 'فئة الإيداع',
    'expense reference': 'مرجع المصروف',
    'deposit reference': 'مرجع الإيداع',
    account_id: 'الحساب',
    account: 'الحساب',
    'bank account': 'الحساب البنكي',
    'from account': 'الحساب المصدر',
    'to account': 'الحساب الهدف',
    'account name': 'اسم الحساب',
    'account number': 'رقم الحساب',
    'initial balance': 'الرصيد الافتتاحي',
    'statement balance': 'رصيد الكشف',
    'statement date': 'تاريخ الكشف',
    currency_id: 'العملة',
    currency: 'العملة',
    grand_total: 'الإجمالي الكلي',
    items: 'العناصر',
    status: 'الحالة',
    employee: 'الموظف',
    'employee id': 'الموظف',
    'shift name': 'اسم الوردية',
    company: 'الشركة',
    'holiday title': 'عنوان العطلة',
    'start date': 'تاريخ البدء',
    'end date': 'تاريخ الانتهاء',
    'settlement date': 'تاريخ التسوية',
    'last working date': 'آخر يوم عمل',
    'period start': 'بداية الفترة',
    'period end': 'نهاية الفترة',
    'fiscal period name': 'اسم الفترة المالية',
    'department name': 'اسم القسم',
    department: 'القسم',
    'designation name': 'اسم المسمى الوظيفي',
    designation: 'المسمى الوظيفي',
    'leave type': 'نوع الإجازة',
    'leave type title': 'عنوان نوع الإجازة',
    reason: 'السبب',
    role: 'الدور',
    'first name': 'الاسم الأول',
    'last name': 'اسم العائلة',
    'admin first name': 'الاسم الأول للمدير',
    'admin email': 'البريد الإلكتروني للمدير',
    'admin password': 'كلمة مرور المدير',
    username: 'اسم المستخدم',
    domain: 'النطاق',
    plan: 'الخطة',
    key: 'المفتاح',
    type: 'النوع',
    tenant: 'المؤسسة',
    'journal date': 'تاريخ القيد',
    'journal type': 'نوع القيد',
    'journal lines': 'بنود القيد',
    'brand name': 'اسم العلامة التجارية',
    'category name': 'اسم الفئة',
    'category title': 'عنوان الفئة',
    'company name': 'اسم الشركة',
    'company email': 'البريد الإلكتروني للشركة',
    'company phone': 'هاتف الشركة',
    details: 'التفاصيل',
    'user id': 'المستخدم',
    user: 'المستخدم',
    reference: 'المرجع',
    'expense category': 'فئة المصروف',
    'total items quantity': 'إجمالي الكمية',
    'unit name': 'اسم الوحدة',
    'short name': 'الاسم المختصر',
    title: 'العنوان',
    description: 'الوصف',
    image: 'الصورة',
    file: 'الملف',
    gateway: 'البوابة',
    'sms gateway': 'بوابة الرسائل',
    settings: 'الإعدادات',
    data: 'البيانات',
    sale: 'البيع',
    purchase: 'المشتريات',
    report: 'التقرير',
    reports: 'التقارير',
    'sms configuration': 'إعدادات الرسائل',
    'sms template': 'قالب الرسائل',
    'email template': 'قالب البريد',
    'mail settings': 'إعدادات البريد',
    cache: 'ذاكرة التخزين المؤقت',
    'pos settings': 'إعدادات نقاط البيع',
  }
  return i18n.language.split('-')[0] === 'ar' ? ATTR_AR_MAP[normalizedKey] || ATTR_AR_MAP[attr.toLowerCase()] || attr : attr
}
